import * as React from "react";
import { Button, Chip, Input, Label, cn } from "@moanhot/ui";
import { useAdminApi, type AdminPriceList } from "@/api/adminClient";
import { NUM, TD, TH, dollars, inputToMicros, microsToInput, when } from "@/screens/format";
import { useAdminAction, useAdminLoad } from "@/screens/hooks";
import { EmptyNote, LoadState, Page, Problem, Table } from "@/screens/ui";

const STATUS_TONE: Record<string, string> = { active: "text-ok", scheduled: "text-info", superseded: "text-ink-3", draft: "text-ink-2" };

const rowKey = (slug: string, variant: string) => `${slug}|${variant}`;

/** A new version from the one shown: changed prices, a date, a note. Owner admins only. */
function NewVersion({ base, onDone }: { base: AdminPriceList; onDone: (v: AdminPriceList) => void }) {
  const api = useAdminApi();
  const { busy, problem, setProblem, run } = useAdminAction();
  return (
    <form
      className="mt-6 rounded-lg border border-line p-5"
      onSubmit={(e) => {
        e.preventDefault();
        const data = new FormData(e.currentTarget);
        const date = String(data.get("effective_at") ?? "");
        if (!date) return setProblem("Pick the date the new prices take effect (UTC).");
        const prices = [];
        for (const p of base.prices) {
          const raw = String(data.get(rowKey(p.effect_slug, p.variant_key)) ?? "").trim();
          if (raw === "") continue;
          const micros = inputToMicros(raw);
          if (micros === null) return setProblem(`The price of ${p.effect_slug} ${p.variant_key} is a dollar amount, 0 or more, with at most six decimals.`);
          if (micros !== p.price_micro_usd) prices.push({ effect_slug: p.effect_slug, variant_key: p.variant_key, price_micro_usd: micros });
        }
        if (prices.length === 0) return setProblem("Change at least one price.");
        run(api.createPriceList({ effective_at: `${date}:00Z`, notes: String(data.get("notes") ?? "") || null, prices }), onDone);
      }}
    >
      <h2 className="text-md font-medium">New version from v{base.version}</h2>
      <p className="mt-1 max-w-[72ch] text-sm text-ink-2">
        A saved version is never edited. Change the prices below and pick a date: from then on new generations use them. Generations already made keep the price they were charged.
      </p>
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="pl-date">Takes effect (UTC)</Label>
          <Input id="pl-date" name="effective_at" type="datetime-local" required className="font-mono" />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="pl-notes">Note</Label>
          <Input id="pl-notes" name="notes" maxLength={500} />
        </div>
      </div>
      <div className="mt-4">
        <Table label="New prices" head={<><th className={TH}>Effect</th><th className={TH}>Variant</th><th className={cn(TH, "text-right")}>Now</th><th className={cn(TH, "text-right")}>New (USD)</th></>}>
          {base.prices.map((p) => (
            <tr key={rowKey(p.effect_slug, p.variant_key)}>
              <td className={TD}>{p.effect_slug}</td>
              <td className={cn(TD, "font-mono text-xs")}>{p.variant_key}</td>
              <td className={NUM}>{dollars(p.price_micro_usd)}</td>
              <td className="py-1.5 pr-4 text-right">
                <Input
                  aria-label={`New price ${p.effect_slug} ${p.variant_key}`}
                  name={rowKey(p.effect_slug, p.variant_key)}
                  inputMode="decimal"
                  defaultValue={microsToInput(p.price_micro_usd)}
                  className="ml-auto w-28 text-right font-mono"
                />
              </td>
            </tr>
          ))}
        </Table>
      </div>
      <div className="mt-4 space-y-2">
        <Problem text={problem} />
        <Button type="submit" variant="primary" disabled={busy}>
          {busy ? "Saving" : "Save new version"}
        </Button>
      </div>
    </form>
  );
}

/** Why the new-version form is not on this version, or null when it is. */
function whyNoForm(owner: boolean, version: number, base: number | null): string | null {
  if (!owner) return "Only an owner admin can create a price list version.";
  if (base !== null && version !== base) return `New versions start from v${base}, the newest one in force or scheduled. Open it to change prices.`;
  return null;
}

function Version({ version, owner, base, onCreated }: { version: number; owner: boolean; base: number | null; onCreated: (v: AdminPriceList) => void }) {
  const [load] = useAdminLoad((api) => api.getPriceList(version), [version], "Could not load this price list.");
  const why = whyNoForm(owner, version, base);
  return (
    <LoadState load={load}>
      {(list) => (
        <>
          <Table
            label={`Price list v${list.version}`}
            head={
              <>
                <th className={TH}>Effect</th>
                <th className={TH}>Variant</th>
                <th className={cn(TH, "text-right")}>Price</th>
                <th className={cn(TH, "text-right")}>Measured GPU cost</th>
                <th className={cn(TH, "text-right")}>Margin</th>
              </>
            }
          >
            {list.prices.map((p) => (
              <tr key={rowKey(p.effect_slug, p.variant_key)}>
                <td className={TD}>{p.effect_slug}</td>
                <td className={cn(TD, "font-mono text-xs")}>
                  {p.variant_key}
                  {p.lane && <div className="text-ink-3">{p.lane}</div>}
                </td>
                <td className={NUM}>{dollars(p.price_micro_usd)}</td>
                <td className={NUM}>
                  {p.measured_cost_micro_usd === null ? <span className="font-sans text-ink-3">Not measured</span> : dollars(p.measured_cost_micro_usd)}
                  <div className="font-sans text-xs text-ink-3">{p.measured_runs} runs, 30 d</div>
                </td>
                <td className={cn(NUM, p.margin_pct !== null && p.margin_pct < 30 && "text-err")}>{p.margin_pct === null ? "" : `${p.margin_pct.toFixed(1)}%`}</td>
              </tr>
            ))}
          </Table>
          {why === null ? <NewVersion base={list} onDone={onCreated} /> : <p className="mt-6 text-sm text-ink-2">{why}</p>}
        </>
      )}
    </LoadState>
  );
}

/** Versioned prices per effect variant, next to what each run measurably costs. */
export function Prices() {
  const [load, reload] = useAdminLoad(
    async (api, session) => ({ ...(await api.listPriceLists()), owner: session.admin.role === "owner" }),
    [],
    "Could not load price lists.",
  );
  const [picked, setPicked] = React.useState<number | null>(null);
  const [notice, setNotice] = React.useState<string | null>(null);

  return (
    <Page title="Prices">
      {notice && (
        <p role="status" className="mb-4 text-sm text-ink-2">
          {notice}
        </p>
      )}
      <LoadState load={load} empty={(d) => (d.data.length === 0 ? <EmptyNote title="No price list yet" body="Import a catalogue bundle with prices to create the first version." /> : null)}>
        {(d) => {
          const current = picked ?? d.data.find((v) => v.status === "active")?.version ?? d.data[0].version;
          // The server builds a new version on the newest scheduled or active one.
          const base = d.data.find((v) => v.status === "scheduled" || v.status === "active")?.version ?? null;
          return (
            <div className="grid gap-6 lg:grid-cols-[220px_1fr]">
              <ul aria-label="Versions" className="space-y-px">
                {d.data.map((v) => (
                  <li key={v.version}>
                    <button
                      type="button"
                      aria-current={v.version === current ? "true" : undefined}
                      onClick={() => setPicked(v.version)}
                      className={cn("w-full rounded-sm px-3 py-2 text-left text-sm hover:bg-hover", v.version === current && "bg-selected")}
                    >
                      <span className="font-medium">v{v.version}</span> <Chip className={STATUS_TONE[v.status] ?? "text-ink-2"}>{v.status}</Chip>
                      <div className="text-xs text-ink-3">{when(v.effective_at)}</div>
                    </button>
                  </li>
                ))}
              </ul>
              <div className="min-w-0">
                <Version
                  key={current}
                  version={current}
                  owner={d.owner}
                  base={base}
                  onCreated={(v) => {
                    setNotice(v.status === "active" ? `Version ${v.version} is active now.` : `Version ${v.version} is scheduled for ${when(v.effective_at)}.`);
                    setPicked(v.version);
                    reload();
                  }}
                />
              </div>
            </div>
          );
        }}
      </LoadState>
    </Page>
  );
}
